import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from "@mui/material"
import { Row } from "@components/VerticalTable"
import PropertiesTable, { NodeProperties } from "@features/graph/details/components/PropertiesTable"
import { Node, useStoreApi } from "reactflow"
import { EditType } from "@components/EditableCell"
import { BaseGraphMetaZipNode } from "@store/services/converter/types"
import { graphReplaceNode } from "@features/graph/state/graphReplaceNode"
import { GraphStoreType } from "@features/graph/Graph"
import { deepCopy } from "@features/graph/utils/graphUtils"
import React, { SyntheticEvent, useEffect, useState } from "react"
import { findNode } from "utils/graph"
import { GraphDataViewModel, GraphEditMode } from "@features/graph/graphViewModel"
import { ParentChildDetails } from "@features/graph/details/components/ParentChildDetails/ParentChildDetails"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useLocalStorage } from "usehooks-ts"
import { NodeRowsAccordion } from "@features/graph/details/components/NodeRowsAccordion"
import { getNodeTypeMap } from "@features/graph/utils/graphNodeUtils"
import { NodeDetailsLabel } from "@features/graph/details/components/NodeDetailsLabel"
import NodeTagsAccordion from "@features/graph/details/components/NodeTagsAccordion"

// https://tkrisk.pgm-np.spglobal.com/docs/graph_creation/nodes_definition.html#discrete-nodes-definition
// example of "properties" to help understand its structure
// {
//   parents_lst: ["n2", "n3", "n4"],
//   a_sample_value: null, // number or null
//   df_cpd: {
//     distribution: ["Binomial", "Binomial"],

//     n: [1, 1],
//     p: [0.5, 0.5],
//     loc: [0, 0],
//     lbound: [null, null],
//     ubound: [null, null],

//     n2: [0, 1], // discrete node parent combinations
//     n3: [4, 5], // deterministic node parent values
//     n4: ["categorical_param1", "categorical_param2"], // categorical node parent selection
//   },
// }

interface DiscreteNodeDetailsProps {
  nodeId: string
  editMode: GraphEditMode
  graphKey?: string
}

type DiscreteNodeParamRecord = Record<string, number[]>
type DiscreteNodeProperties = NodeProperties<DiscreteNodeParamRecord, number>

export const DiscreteNodeDetails = ({ nodeId, graphKey, editMode }: DiscreteNodeDetailsProps) => {
  const graphStore: GraphStoreType = useStoreApi()
  const [node, setNode] = useState<Node>()
  const [expandedPropertiesTable, setExpandedPropertiesTable] = useLocalStorage(
    `nodePropertiesTableAccordionExpanded`,
    true,
  )

  useEffect(() => {
    setNode(findNode(graphStore, nodeId))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [nodeId])

  if (!node) {
    return
  }

  const properties: DiscreteNodeProperties = node.data.node?.properties ?? {}
  const sampleValue = properties.a_sample_value
  const nodeTypeMap = getNodeTypeMap(GraphDataViewModel.data?.nodeMap ?? {})

  const { unit, Description, label, color } = node.data?.display ?? {}

  const handleDisplayFieldCellBlur = async (
    field: keyof BaseGraphMetaZipNode,
    newValue: string,
  ) => {
    if (!graphKey) {
      return
    }

    node.data.display[field] = newValue
    await graphReplaceNode(graphStore, graphKey, [node])

    setNode(deepCopy(node))
  }

  const handlePropertiesFieldCellBlur = async (field: string, newValue: string) => {
    if (!graphKey) {
      return
    }

    node.data.node.properties[field] = Number(newValue)
    await graphReplaceNode(graphStore, graphKey, [node])

    setNode(deepCopy(node))
  }

  const handleDfCpdFieldCellBlur = async (field: string, newValue: string, index: number) => {
    if (!graphKey) {
      return
    }

    const isOptionValue = node.data.node.properties.parents_lst.includes(newValue)
    const parsedValue = isOptionValue ? newValue : Number(newValue)

    node.data.node.properties.df_cpd[field][index] = parsedValue
    await graphReplaceNode(graphStore, graphKey, [node])

    setNode(deepCopy(node))
  }

  const nodeRows: Row[] = [
    { label: "Type", value: "Discrete" },
    // {
    //   label: "Color",
    //   children: <ColorPreview color={color} />,
    //   value: color,
    //   editable: editMode === GraphEditMode.DATA,
    //   type: EditType.Color,
    //   onBlur: (newValue) => handleDisplayFieldCellBlur("color", newValue),
    // },
    { label: "Historic Mnemonic", value: node.data.display["Historic Mnem"] },
    {
      label: "Description",
      children: Description || "-",
      value: Description,
      editable: editMode === GraphEditMode.DATA,
      onBlur: (newValue) => handleDisplayFieldCellBlur("Description", newValue),
    },
    {
      label: "Unit",
      children: formatValuePythonic("", node.data?.display["Unit Format"]) || "-",
      value: formatValuePythonic("", node.data?.display["Unit Format"]) || "-",
      editable: editMode === GraphEditMode.DATA,
      onBlur: (newValue) => handleDisplayFieldCellBlur("unit", newValue),
    },
    { label: "Exclude From Export", value: node.data?.display.exportExcluded ? "yes" : "no" },
    { label: "Link", value: node.data.display.Link ?? "" },
  ]

  if (sampleValue !== null) {
    nodeRows.push({
      label: "Sample value",
      value: sampleValue,
      editable: editMode === GraphEditMode.SCENARIO,
      type: EditType.Number,
      onBlur: (newValue) => handlePropertiesFieldCellBlur("a_sample_value", newValue),
    })
  }

  return (
    <Grid container direction="column" sx={{ p: 1 }} spacing={1}>
      <Grid item>
        <NodeDetailsLabel label={label} />
      </Grid>
      <NodeRowsAccordion nodeRows={nodeRows} />
      <NodeTagsAccordion tags={node.data.display.tags ?? []} />
      <Grid item sx={{ width: expandedPropertiesTable ? "auto" : "100%" }}>
        <Accordion
          expanded={expandedPropertiesTable}
          onChange={(event: SyntheticEvent, isExpanded: boolean) => {
            setExpandedPropertiesTable(isExpanded)
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>Properties Table</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <PropertiesTable
              type={node.data.node.type}
              editable={editMode === GraphEditMode.SCENARIO}
              labelMap={node.data.labelMap}
              nodeTypeMap={nodeTypeMap}
              properties={properties}
              onBlur={handleDfCpdFieldCellBlur}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <ParentChildDetails />
    </Grid>
  )
}
